import { z } from 'zod';
import { difficultySchema, exerciseKindSchema, interviewCategorySchema } from '../enums.js';

/**
 * Admin content management.
 *
 * These schemas are deliberately separate from the learner-facing ones rather
 * than shared with an extra field. The learner contracts must never carry
 * `solutionSql`, and a test asserts exactly that — keeping the admin shapes in
 * their own file means an editor field can never leak into a learner response
 * by someone widening a schema that both sides happen to use.
 */

const slugSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase words separated by single hyphens');

// --- The content tree -------------------------------------------------------

export const adminExerciseRowSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  kind: exerciseKindSchema,
  difficulty: difficultySchema,
  datasetSlug: z.string(),
  xpReward: z.number().int(),
  sortOrder: z.number().int(),
  isPublished: z.boolean(),
  hintCount: z.number().int(),
  /** Completions block deletion, so the tree shows the count up front. */
  completionCount: z.number().int(),
});
export type AdminExerciseRow = z.infer<typeof adminExerciseRowSchema>;

export const adminLessonRowSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  estimatedMinutes: z.number().int(),
  xpReward: z.number().int(),
  sortOrder: z.number().int(),
  isPublished: z.boolean(),
  exercises: z.array(adminExerciseRowSchema),
});
export type AdminLessonRow = z.infer<typeof adminLessonRowSchema>;

export const adminModuleRowSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  level: z.number().int(),
  sortOrder: z.number().int(),
  isPublished: z.boolean(),
  lessons: z.array(adminLessonRowSchema),
});
export type AdminModuleRow = z.infer<typeof adminModuleRowSchema>;

export const adminCourseRowSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  sortOrder: z.number().int(),
  isPublished: z.boolean(),
  modules: z.array(adminModuleRowSchema),
});
export type AdminCourseRow = z.infer<typeof adminCourseRowSchema>;

export const adminDatasetRowSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  schemaName: z.string(),
  isPublished: z.boolean(),
  tableCount: z.number().int(),
  /** Exercises that depend on it; a dataset in use cannot be unpublished. */
  exerciseCount: z.number().int(),
});
export type AdminDatasetRow = z.infer<typeof adminDatasetRowSchema>;

export const adminContentTreeSchema = z.object({
  courses: z.array(adminCourseRowSchema),
  datasets: z.array(adminDatasetRowSchema),
  /** Interview questions belong to no lesson, so they hang off the root. */
  interviewQuestions: z.array(adminExerciseRowSchema),
});
export type AdminContentTree = z.infer<typeof adminContentTreeSchema>;

// --- Editable detail --------------------------------------------------------

export const adminHintSchema = z.object({
  id: z.uuid(),
  position: z.number().int(),
  bodyMd: z.string(),
  xpPenalty: z.number().int(),
});
export type AdminHint = z.infer<typeof adminHintSchema>;

/**
 * The full exercise, solution included. Only ever returned behind the admin
 * guard — see the note at the top of this file.
 */
export const adminExerciseDetailSchema = adminExerciseRowSchema.extend({
  lessonId: z.uuid().nullable(),
  datasetId: z.uuid(),
  promptMd: z.string(),
  explanationMd: z.string(),
  starterSql: z.string(),
  solutionSql: z.string(),
  orderMatters: z.boolean(),
  compareColumnNames: z.boolean(),
  numericTolerance: z.number(),
  expectedColumns: z.array(z.string()).nullable(),
  tags: z.array(z.string()),
  interviewCategory: z.string().nullable(),
  timeLimitSeconds: z.number().int().nullable(),
  hints: z.array(adminHintSchema),
});
export type AdminExerciseDetail = z.infer<typeof adminExerciseDetailSchema>;

export const adminLessonDetailSchema = adminLessonRowSchema.omit({ exercises: true }).extend({
  moduleId: z.uuid(),
  contentMd: z.string(),
});
export type AdminLessonDetail = z.infer<typeof adminLessonDetailSchema>;

export const adminModuleDetailSchema = adminModuleRowSchema.omit({ lessons: true }).extend({
  courseId: z.uuid(),
  summaryMd: z.string(),
});
export type AdminModuleDetail = z.infer<typeof adminModuleDetailSchema>;

// --- Writes -----------------------------------------------------------------

export const createModuleSchema = z.object({
  courseId: z.uuid(),
  slug: slugSchema,
  title: z.string().min(1).max(200),
  summaryMd: z.string().max(20_000).default(''),
  level: z.number().int().min(1).max(20),
  sortOrder: z.number().int().min(0).default(0),
});
export type CreateModuleRequest = z.infer<typeof createModuleSchema>;

export const updateModuleSchema = createModuleSchema
  .omit({ courseId: true })
  .partial()
  .extend({ isPublished: z.boolean().optional() });
export type UpdateModuleRequest = z.infer<typeof updateModuleSchema>;

export const createLessonSchema = z.object({
  moduleId: z.uuid(),
  slug: slugSchema,
  title: z.string().min(1).max(200),
  contentMd: z.string().min(1).max(200_000),
  estimatedMinutes: z.number().int().min(1).max(600).default(8),
  xpReward: z.number().int().min(0).max(1000).default(10),
  sortOrder: z.number().int().min(0).default(0),
});
export type CreateLessonRequest = z.infer<typeof createLessonSchema>;

export const updateLessonSchema = createLessonSchema
  .partial()
  .extend({ isPublished: z.boolean().optional() });
export type UpdateLessonRequest = z.infer<typeof updateLessonSchema>;

export const createExerciseSchema = z.object({
  lessonId: z.uuid().nullable().default(null),
  datasetId: z.uuid(),
  slug: slugSchema,
  title: z.string().min(1).max(200),
  kind: exerciseKindSchema.default('lesson'),
  difficulty: difficultySchema,
  promptMd: z.string().min(1).max(20_000),
  explanationMd: z.string().max(20_000).default(''),
  starterSql: z.string().max(10_000).default(''),
  solutionSql: z.string().min(1).max(10_000),
  orderMatters: z.boolean().default(false),
  compareColumnNames: z.boolean().default(false),
  numericTolerance: z.number().min(0).max(1000).default(0.01),
  expectedColumns: z.array(z.string().min(1).max(120)).max(50).nullable().default(null),
  xpReward: z.number().int().min(0).max(1000).default(20),
  tags: z.array(z.string().min(1).max(40)).max(20).default([]),
  sortOrder: z.number().int().min(0).default(0),
  interviewCategory: interviewCategorySchema.nullable().default(null),
  timeLimitSeconds: z.number().int().min(30).max(7200).nullable().default(null),
});
export type CreateExerciseRequest = z.infer<typeof createExerciseSchema>;

export const updateExerciseSchema = createExerciseSchema
  .partial()
  .extend({ isPublished: z.boolean().optional() });
export type UpdateExerciseRequest = z.infer<typeof updateExerciseSchema>;

/** Hints are replaced wholesale: they are positional, and reordering by patch is worse. */
export const replaceHintsSchema = z.object({
  hints: z
    .array(
      z.object({
        bodyMd: z.string().min(1).max(4000),
        xpPenalty: z.number().int().min(0).max(500).default(0),
      }),
    )
    .max(10),
});
export type ReplaceHintsRequest = z.infer<typeof replaceHintsSchema>;

export const updateCourseSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  descriptionMd: z.string().max(20_000).optional(),
  sortOrder: z.number().int().min(0).optional(),
  isPublished: z.boolean().optional(),
});
export type UpdateCourseRequest = z.infer<typeof updateCourseSchema>;

/**
 * Datasets are generated, not authored: their schema and tables come from the
 * generators in `packages/database`. Only the presentation and the publish flag
 * are editable here.
 */
export const updateDatasetSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).optional(),
  isPublished: z.boolean().optional(),
});
export type UpdateDatasetRequest = z.infer<typeof updateDatasetSchema>;

/**
 * The result of checking a solution against the live sandbox. Authoring an
 * exercise whose own solution does not run is the single worst content bug, so
 * the editor can ask before saving.
 */
export const solutionCheckSchema = z.object({
  ok: z.boolean(),
  rowCount: z.number().int().nullable(),
  columns: z.array(z.string()),
  executionMs: z.number().int().nullable(),
  message: z.string().nullable(),
});
export type SolutionCheck = z.infer<typeof solutionCheckSchema>;

export const checkSolutionRequestSchema = z.object({
  datasetId: z.uuid(),
  solutionSql: z.string().min(1).max(10_000),
});
export type CheckSolutionRequest = z.infer<typeof checkSolutionRequestSchema>;
